import React from 'react';
import { motion } from 'framer-motion';

const SATPredictionAnalysis = ({ analysisData }) => {
    // Validate required data structure
    const isValidData = analysisData && 
        analysisData.studyTimeRequired?.months &&
        analysisData.achievabilityRate?.percentage &&
        analysisData.furtherGrowthPotential?.percentage;

    // Early return if data is invalid
    if (!isValidData) {
        return (
            <div className="w-full p-4 text-center text-slate-300">
                <p>Analysis data is being processed...</p>
            </div>
        );
    }

    // Helper function to get color based on percentage
    const getPercentageColor = (percentage) => {
        if (percentage >= 80) return 'text-green-500';
        if (percentage >= 60) return 'text-blue-500';
        if (percentage >= 40) return 'text-yellow-500';
        if (percentage >= 20) return 'text-orange-500';
        return 'text-red-500';
    };

    // Helper function to format months text
    const formatMonths = (months) => {
        return `${months} month${months > 1 ? 's' : ''}`;
    };

    return (
        <div className="w-full max-w-4xl">
            <div className="text-slate-100">
                {/* Header */}
                <div className="border-b border-[#3b3b3b] px-4 py-2">
                    <h2 className="text-xl font-bold">SAT Score Prediction Analysis</h2>
                </div>

                {/* Content */}
                <div className="px-4 py-2">
                    <div className="grid grid-cols-1 gap-4">
                        {/* Study Time Required */}
                        <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] transition-colors duration-200">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-semibold">Required Study Time</h3>
                                <span className="text-xl font-bold text-[#87bfff]">
                                    {formatMonths(analysisData.studyTimeRequired.months)}
                                </span>
                            </div>
                            <div
                                className="text-sm text-slate-300"
                                dangerouslySetInnerHTML={{
                                    __html: analysisData.studyTimeRequired.detail.replace(
                                        /\*\*(.*?)\*\*/g,
                                        '<span class="font-bold text-[#87bfff]">$1</span>'
                                    ),
                                }}
                            />
                        </div>

                        {/* Achievement Rate */}
                        <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] transition-colors duration-200">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-semibold">Success Rate</h3>
                                <span className={`text-xl font-bold ${getPercentageColor(analysisData.achievabilityRate.percentage)}`}>
                                    {analysisData.achievabilityRate.percentage}%
                                </span>
                            </div>
                            <div
                                className="text-sm text-slate-300"
                                dangerouslySetInnerHTML={{
                                    __html: analysisData.achievabilityRate.reason.replace(
                                        /\*\*(.*?)\*\*/g,
                                        '<span class="font-bold text-[#87bfff]">$1</span>'
                                    ),
                                }}
                            />
                        </div>

                        {/* Growth Potential */}
                        <div className="bg-[#2b2b2b] rounded-lg p-4 border border-[#3b3b3b] hover:border-[#87bfff] transition-colors duration-200">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-semibold">Growth Potential</h3>
                                <span className={`text-xl font-bold ${getPercentageColor(analysisData.furtherGrowthPotential.percentage)}`}>
                                    {analysisData.furtherGrowthPotential.percentage}%
                                </span>
                            </div>
                            <div
                                className="text-sm text-slate-300"
                                dangerouslySetInnerHTML={{
                                    __html: analysisData.furtherGrowthPotential.explanation.replace(
                                        /\*\*(.*?)\*\*/g,
                                        '<span class="font-bold text-[#87bfff]">$1</span>'
                                    ),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SATPredictionAnalysis;